/**
 * Notification Context
 * Manages notification state and real-time updates across the application
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import notificationService from "../shared/services/notificationService";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();

  // State - single array for recent notifications (15 max)
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

  // Load recent notifications (15 max) - called once on login
  const loadRecentNotifications = useCallback(async () => {
    if (!isLoggedIn || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const recentNotifications =
        await notificationService.getRecentNotifications(15);
      const formattedNotifications = recentNotifications.map(
        notificationService.formatNotification,
      );

      setNotifications(formattedNotifications);

      // Count unread notifications (use isRead from formatted notifications)
      const unread = formattedNotifications.filter((n) => !n.isRead).length;
      setUnreadCount(unread);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId, actionTaken = null) => {
    try {
      // Optimistic update: Mark as read immediately in local state
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true, _optimisticRead: true }
            : notification,
        ),
      );

      // Update unread count optimistically
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Make API call
      await notificationService.markAsRead(notificationId, actionTaken);

      // Remove optimistic flag after successful API call
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, _optimisticRead: false }
            : notification,
        ),
      );

      // Safety timeout: Clear optimistic flag after 5 seconds even if no real-time update
      setTimeout(() => {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, _optimisticRead: false }
              : notification,
          ),
        );
      }, 5000);
    } catch (err) {
      // Revert optimistic update on error
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: false, _optimisticRead: false }
            : notification,
        ),
      );

      // Revert unread count
      setUnreadCount((prev) => prev + 1);

      throw err;
    }
  }, []);

  // Mark all notifications as read.
  // (Previously wrapped in try/catch that just rethrew — useless; removed.)
  const markAllAsRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, isRead: true })),
    );
    setUnreadCount(0);
  }, []);

  // Handle real-time notification updates
  const handleRealtimeUpdate = useCallback((event, data) => {
    switch (event) {
      case "new_notification": {
        const newNotification = notificationService.formatNotification(data);
        setNotifications((prev) => [newNotification, ...prev.slice(0, 14)]); // Keep only 15 total

        setUnreadCount((prev) => prev + 1);

        // Trigger auto-open of notification dropdown
        setShouldAutoOpen(true);

        // Show browser notification if permission granted
        // Use BroadcastChannel to coordinate between tabs - only one tab shows the notification
        if (Notification.permission === "granted") {
          const channel = new BroadcastChannel("cluck_notifications");

          // If this tab is focused, claim the notification immediately
          if (document.hasFocus()) {
            new Notification(newNotification.title, {
              body: newNotification.message,
              icon: "/favicon.ico",
              tag: newNotification.id,
            });
            channel.postMessage({
              type: "notification_shown",
              id: newNotification.id,
            });
          } else {
            // If no tab is focused, wait a bit and show if no other tab claimed it
            const timeoutId = setTimeout(() => {
              channel.postMessage({
                type: "notification_request",
                id: newNotification.id,
              });

              // If still no response after 100ms, show it from this tab
              setTimeout(() => {
                new Notification(newNotification.title, {
                  body: newNotification.message,
                  icon: "/favicon.ico",
                  tag: newNotification.id,
                });
              }, 100);
            }, 50);

            // Listen for other tabs claiming the notification
            const handleMessage = (e) => {
              if (
                e.data.type === "notification_shown" &&
                e.data.id === newNotification.id
              ) {
                clearTimeout(timeoutId);
                channel.removeEventListener("message", handleMessage);
              }
            };
            channel.addEventListener("message", handleMessage);
          }

          channel.close();
        }
        break;
      }

      case "notification_updated": {
        const updatedNotification =
          notificationService.formatNotification(data);
        setNotifications((prev) =>
          prev.map((notification) => {
            if (notification.id === updatedNotification.id) {
              // If we have an optimistic read update in progress, don't overwrite it
              // unless the database confirms the read status
              if (notification._optimisticRead && !updatedNotification.isRead) {
                // Keep optimistic state, database hasn't caught up yet
                return notification;
              }

              // Normal update - database is authoritative
              return {
                ...updatedNotification,
                _optimisticRead: false, // Clear any optimistic flags
              };
            }
            return notification;
          }),
        );

        // Update unread count based on actual read status changes
        setUnreadCount((prev) => {
          const currentNotification = notifications.find(
            (n) => n.id === updatedNotification.id,
          );
          if (currentNotification) {
            // If notification was unread and now is read, decrease count
            if (!currentNotification.isRead && updatedNotification.isRead) {
              return Math.max(0, prev - 1);
            }
            // If notification was read and now is unread (shouldn't happen but handle it)
            if (currentNotification.isRead && !updatedNotification.isRead) {
              return prev + 1;
            }
          }
          return prev;
        });

        break;
      }

      default:
        break;
    }
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return Notification.permission === "granted";
  }, []);

  // Check for auto-open when unread count changes
  useEffect(() => {
    if (isLoggedIn && unreadCount > 0) {
      setShouldAutoOpen(true);
    }
  }, [isLoggedIn, unreadCount]);

  // Setup real-time subscriptions when user logs in
  useEffect(() => {
    if (isLoggedIn && user?.id) {
      // Load initial recent notifications (15 max)
      loadRecentNotifications();

      // Subscribe to real-time updates
      notificationService.subscribeToNotifications(
        user.id,
        handleRealtimeUpdate,
      );

      // Add service listener for local events
      notificationService.addListener(handleRealtimeUpdate);

      // Request notification permission
      requestNotificationPermission();

      return () => {
        notificationService.unsubscribeFromNotifications();
        notificationService.removeListener(handleRealtimeUpdate);
      };
    } else {
      // Clear state when user logs out
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      setShouldAutoOpen(false);
    }
  }, [
    isLoggedIn,
    user?.id,
    loadRecentNotifications,
    handleRealtimeUpdate,
    requestNotificationPermission,
  ]);

  // Function to clear auto-open state (called when dropdown is opened)
  const clearAutoOpen = useCallback(() => {
    setShouldAutoOpen(false);
  }, []);

  const value = {
    // State - single notifications array (recent 15)
    notifications,
    unreadCount,
    loading,
    error,
    shouldAutoOpen,

    // Actions
    loadRecentNotifications,
    markAsRead,
    markAllAsRead,
    requestNotificationPermission,
    clearAutoOpen,

    // Utilities
    formatNotification: notificationService.formatNotification,
    getTimeAgo: notificationService.getTimeAgo,
    getPriorityColor: notificationService.getPriorityColor,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
